import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Share2, Tag as TagIcon, Clock } from "lucide-react";
import { NextInPath, PathResumeBanner, ReadSentinel } from "@/components/StartHere";
import { START_HERE } from "@/lib/startHere";
import { fetchBlogPost, blogHeroUrl, blogOgUrl, API, recordExperimentEvent } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BlogSignupGate from "@/components/BlogSignupGate";
import NewsletterCapture from "@/components/NewsletterCapture";
import TrustpilotCollector from "@/components/TrustpilotCollector";
import Comments from "@/components/Comments";
import ShareStrip from "@/components/ShareStrip";
import { toast } from "sonner";

/** Trim Markdown body to the first N paragraphs.
 *  Paragraphs are separated by blank lines. Very simple, but robust to
 *  headings and blockquotes because we only count non-empty text blocks. */
function previewParagraphs(md, count = 2) {
  if (!md) return "";
  const blocks = md.split(/\n\s*\n/);
  const out = [];
  for (const b of blocks) {
    if (b.trim()) out.push(b);
    if (out.length >= count) break;
  }
  return out.join("\n\n");
}

/** Sticky A/B assignment across visits. Coin-flip on first render. */
function usePaywallVariant() {
  const [variant, setVariant] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  useEffect(() => {
    try {
      let v = localStorage.getItem("umbra_paywall_variant");
      if (v !== "2p" && v !== "3p") {
        v = Math.random() < 0.5 ? "2p" : "3p";
        localStorage.setItem("umbra_paywall_variant", v);
      }
      let sid = localStorage.getItem("umbra_paywall_session");
      if (!sid) {
        // 16 hex chars, no PII
        sid = ([1, 2, 3, 4]
          .map(() => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, "0"))
          .join(""));
        localStorage.setItem("umbra_paywall_session", sid);
      }
      setVariant(v);
      setSessionId(sid);
    } catch {
      setVariant("2p");
      setSessionId("nostorage");
    }
  }, []);
  return { variant, sessionId };
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const location = useLocation();
  const previewMode = location.pathname.startsWith("/blog/preview/");
  const sharePreviewMode = location.pathname.startsWith("/blog/share/");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [body, setBody] = useState("");
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const { variant, sessionId } = usePaywallVariant();

  // Record a paywall-view event once per (session, slug) pair.
  useEffect(() => {
    if (!post || user || !variant || !sessionId) return;
    const flag = `umbra_paywall_view_${slug}`;
    try {
      if (sessionStorage.getItem(flag)) return;
      sessionStorage.setItem(flag, "1");
    } catch {
      /* ignore */
    }
    recordExperimentEvent({
      variant,
      event: "view",
      session_id: sessionId,
      post_slug: slug,
    });
  }, [post, user, variant, sessionId, slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        // Shareable preview: pull the ?t= token from the URL and hit the
        // dedicated share-preview endpoint (no admin cookie needed).
        if (sharePreviewMode) {
          const params = new URLSearchParams(location.search);
          const token = params.get("t");
          if (!token) {
            setNotFound(true);
            return;
          }
          const res = await import("@/lib/api").then(m =>
            m.fetchBlogSharePreview(slug, token)
          );
          if (cancelled) return;
          setPost(res.post);
          setBody(res.body || "");
          setRelated(res.related || []);
          setTokenExpiresAt(res.token_expires_at || null);
          return;
        }
        const res = await fetchBlogPost(slug, { preview: previewMode });
        if (cancelled) return;
        // Retired duplicates 301 to the original post at the API; mirror that in the URL.
        if (res.post?.slug && res.post.slug !== slug) {
          navigate(`/blog/${res.post.slug}`, { replace: true });
          return;
        }
        setPost(res.post);
        setBody(res.body || "");
        setRelated(res.related || []);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404 || e?.response?.status === 422) setNotFound(true);
          setPost(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, previewMode, sharePreviewMode, location.search]);

  const canonical = useMemo(() => {
    try {
      return `${window.location.origin}/blog/${slug}`;
    } catch {
      return `/blog/${slug}`;
    }
  }, [slug]);

  const readerUrl = `${API}/blog/html/${slug}`;

  const heroAbs = useMemo(() => {
    if (!post?.hero_url || !post?.id) return null;
    return blogHeroUrl(post.id);
  }, [post]);

  const ogAbs = useMemo(() => {
    if (!post?.id) return null;
    if (post.og_image_url) return blogOgUrl(post.id);
    return heroAbs;
  }, [post, heroAbs]);

  const jsonLd = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.meta_description,
      image: heroAbs ? [heroAbs] : undefined,
      author: { "@type": "Person", name: (typeof post.author === "string" ? post.author : post.author?.name) || "Umbra" },
      publisher: {
        "@type": "Organization",
        name: "Umbra",
        url: "https://shadowspeakjournal.com",
      },
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      keywords: (post.tags || []).join(", "),
      articleSection: post.category,
      wordCount: post.reading_time
        ? Math.round((post.reading_time * 220))
        : undefined,
    };
  }, [post, heroAbs, canonical]);

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url: canonical });
      } else {
        await navigator.clipboard.writeText(canonical);
        toast.success("Link copied");
      }
    } catch {
      /* user cancelled */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen px-6 md:px-12 py-16" data-testid="blog-post-loading">
        <p className="font-sans text-bone-500 text-sm" aria-live="polite">
          Loading…
        </p>
      </div>
    );
  }
  if (notFound || !post) {
    return (
      <div className="min-h-screen px-6 md:px-12 py-16" data-testid="blog-post-notfound">
        <p className="font-serif italic text-bone-400 mb-6">
          This entry has drifted out of the library.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-terracotta hover:text-terracotta-deep focus:outline-none focus-visible:underline"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden="true" /> Back to the journal
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian-900" data-testid="blog-post-page">
      {previewMode && (
        <div
          className="bg-terracotta/10 border-b border-terracotta/60 px-5 py-3"
          data-testid="blog-preview-banner"
        >
          <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-3 justify-between">
            <p className="font-sans uppercase tracking-[0.2em] text-[0.7rem] text-terracotta">
              Preview · publishes {post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—"}
            </p>
            <Link to="/admin/blog" className="font-sans uppercase tracking-[0.2em] text-[0.65rem] text-bone-300 hover:text-terracotta">
              ← Back to admin
            </Link>
          </div>
        </div>
      )}
      {sharePreviewMode && (
        <div
          className="bg-terracotta/10 border-b border-terracotta/60 px-5 py-3"
          data-testid="blog-share-preview-banner"
        >
          <div className="max-w-3xl mx-auto">
            <p className="font-sans uppercase tracking-[0.2em] text-[0.7rem] text-terracotta">
              Shared draft · for proofreading only
              {tokenExpiresAt && (
                <span className="normal-case tracking-normal text-bone-400 ml-2">
                  — link expires {new Date(tokenExpiresAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </p>
            <p className="font-serif italic text-bone-400 text-sm mt-1">
              Please don't post this URL publicly — it's a time-limited preview and the post is not yet live.
            </p>
          </div>
        </div>
      )}
      <Helmet>
        <html lang="en" />
        <title>{`${post.title} — Umbra Journal`}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" type="text/html" title="Reader mode (no JavaScript)" href={readerUrl} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Umbra Journal"
          href={`${API}/blog/feed.xml`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:url" content={canonical} />
        {ogAbs && <meta property="og:image" content={ogAbs} />}
        {ogAbs && post.hero_alt && <meta property="og:image:alt" content={post.hero_alt} />}
        <meta property="article:published_time" content={post.published_at || ""} />
        <meta property="article:modified_time" content={post.updated_at || post.published_at || ""} />
        {post.category && <meta property="article:section" content={post.category} />}
        {(post.tags || []).map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.meta_description} />
        {ogAbs && <meta name="twitter:image" content={ogAbs} />}
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      <a
        href="#post-body"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-terracotta focus:text-obsidian-900 focus:px-3 focus:py-2 focus:font-sans focus:text-xs focus:uppercase focus:tracking-[0.24em]"
      >
        Skip to article
      </a>

      <header className="border-b border-obsidian-600 px-6 md:px-12 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden="true" /> The Journal
          </Link>
          <button
            onClick={share}
            data-testid="blog-post-share"
            aria-label="Share this post"
            className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900"
          >
            <Share2 className="h-3 w-3" aria-hidden="true" /> Share
          </button>
        </div>
      </header>

      <noscript>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-6">
          <p className="font-serif italic text-bone-300 mb-2">
            You have JavaScript disabled. A fully-readable version of this
            post is available:
          </p>
          <a
            href={readerUrl}
            className="font-sans text-sm uppercase tracking-[0.24em] text-terracotta underline"
          >
            Read the plain-text version →
          </a>
        </div>
      </noscript>

      <article className="max-w-[70ch] mx-auto px-6 md:px-12 pt-12 pb-24">
        <PathResumeBanner slug={slug} />
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {post.category && (
            <Link
              to={`/blog/category/${post.category}`}
              className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-terracotta hover:underline focus:outline-none focus-visible:underline"
              data-testid="blog-post-category"
            >
              {post.category}
            </Link>
          )}
          <span className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-bone-500">
            · {formatDate(post.published_at)}
          </span>
          <span className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-bone-500">
            · by {typeof post.author === "string" ? post.author : (post.author?.name || "Umbra")}
          </span>
          {post.reading_time && (
            <span
              className="font-sans text-[0.6rem] uppercase tracking-[0.28em] text-bone-500 inline-flex items-center gap-1"
              data-testid="blog-post-reading-time"
            >
              <Clock className="h-3 w-3" aria-hidden="true" />
              {post.reading_time} min read
            </span>
          )}
        </div>

        <h1
          className="font-serif text-4xl sm:text-5xl text-bone-100 leading-tight mb-4"
          data-testid="blog-post-title"
        >
          {post.title}
        </h1>
        <p className="font-serif italic text-lg text-bone-400 mb-10 leading-relaxed">
          {post.meta_description}
        </p>

        {heroAbs && (
          <figure className="mb-12 -mx-6 md:mx-0">
            <img
              src={heroAbs}
              alt={post.hero_alt || post.title}
              width="1200"
              height="675"
              className="w-full aspect-[16/9] object-cover border border-obsidian-600"
              data-testid="blog-post-hero"
            />
          </figure>
        )}

        <div
          id="post-body"
          className="prose prose-invert max-w-none font-serif text-bone-200
                     text-[1.15rem] leading-[1.75]
                     prose-headings:font-serif prose-headings:text-bone-100 prose-headings:font-medium
                     prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-4
                     prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-3
                     prose-p:my-5
                     prose-a:text-terracotta prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-terracotta-soft
                     prose-strong:text-bone-100 prose-em:text-bone-100
                     prose-blockquote:border-l-terracotta prose-blockquote:text-bone-300
                     prose-code:text-terracotta prose-hr:border-obsidian-600
                     prose-img:border prose-img:border-obsidian-600
                     prose-li:my-2 prose-ul:my-5 prose-ol:my-5"
          data-testid="blog-post-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {(user || previewMode || sharePreviewMode) ? body : previewParagraphs(body, variant === "3p" ? 3 : 2)}
          </ReactMarkdown>
        </div>
        <ReadSentinel slug={slug} />

        <ShareStrip
          url={canonical}
          title={post.title}
          description={post.meta_description}
        />

        {!user && !previewMode && !sharePreviewMode && (
          <BlogSignupGate
            slug={slug}
            title={post.title}
            variant={variant}
            sessionId={sessionId}
            related={related}
          />
        )}

        <NextInPath slug={slug} />

        {slug !== START_HERE[START_HERE.length - 1].slug && (
          <NewsletterCapture source="blog-post" postSlug={slug} />
        )}

        <Comments postSlug={slug} />

        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-obsidian-600">
            <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-3">
              Tags
            </p>
            <div className="flex flex-wrap gap-2" data-testid="blog-post-tags">
              {post.tags.map((t) => (
                <Link
                  key={t}
                  to={`/blog/tag/${t}`}
                  className="font-sans text-[0.65rem] lowercase text-bone-400 border border-obsidian-600 px-2 py-0.5 inline-flex items-center gap-1 hover:border-terracotta hover:text-terracotta focus:outline-none focus-visible:ring-1 focus-visible:ring-terracotta"
                >
                  <TagIcon className="h-2.5 w-2.5" aria-hidden="true" />
                  {t}
                </Link>
              ))}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <aside className="mt-16 pt-10 border-t border-obsidian-600" aria-label="Related posts">
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-6">
              Related reading
            </p>
            <ul
              className="grid grid-cols-1 sm:grid-cols-3 gap-6 list-none p-0"
              data-testid="blog-post-related"
            >
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/blog/${r.slug}`}
                    className="group block border border-obsidian-600 p-5 hover:border-terracotta/70 focus:outline-none focus-visible:border-terracotta transition-colors h-full"
                  >
                    <p className="font-sans text-[0.55rem] uppercase tracking-[0.24em] text-bone-500 mb-2">
                      {formatDate(r.published_at)}
                    </p>
                    <h3 className="font-serif text-lg text-bone-100 group-hover:text-terracotta transition-colors leading-snug">
                      {r.title}
                    </h3>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div className="mt-16 pt-8 border-t border-obsidian-600" data-testid="blog-post-review-ask">
          <p className="font-sans text-sm text-bone-400 mb-3">If Umbra's essays or chapters have helped, a short review helps the next reader find them.</p>
          <TrustpilotCollector id="blog-post-trustpilot-collector" className="max-w-[240px]" />
        </div>
      </article>
    </div>
  );
}
